import {
  MembershipStatus,
  RumorStatus,
} from '../../domain/types';
import { calculateMembershipStatus } from '../../agent/utils/calculateMembershipStatus';
import { isTrialAgent } from '../../utils/agent/isTrialAgent';
import {
  UserPermissionGroup,
  UserPermissions,
} from './types';
import { useCurrentAgentSync } from '../../hooks/use-agent/useAgent';
import useRBAC from '../../hooks/use-rbac/useRBAC';

export type RBAC = ReturnType<typeof useRBAC>;
export type Agent = NonNullable<ReturnType<typeof useCurrentAgentSync>['data']>;
export type PermissionFactory<T extends UserPermissionGroup> = (rbac: RBAC, currentAgent?: Agent) => UserPermissions[T];

function hasRevocationStatus(currentAgent?: Agent): boolean {
  if (!currentAgent) {
    return false;
  }

  const status = calculateMembershipStatus(
    new Date(currentAgent.contract.startDate),
    currentAgent.contract.endDate
      ? new Date(currentAgent.contract.endDate)
      : undefined,
  );

  return [
    MembershipStatus.REVOKED,
    MembershipStatus.UNDER_REVOCATION,
  ].includes(status);
}

const createRumorsPermissions: PermissionFactory<'rumors'> = (rbac) => ({
  canAdd: rbac.userIsAdmin || rbac.userIsCallCenter || rbac.userIsAgent,
  canEdit: (agentIsRelatedToEntity = true) => rbac.userIsAdmin || agentIsRelatedToEntity,
  reminders: {
    canAdd: (rumorStatus, agentIsRelatedToEntity = true) => rumorStatus === RumorStatus.IN_PROGRESS && (rbac.userIsAdmin || agentIsRelatedToEntity),
  },
});

const createJobApplicationsPermissions: PermissionFactory<'jobApplications'> = (rbac, currentAgent) => {
  const revoked = hasRevocationStatus(currentAgent);

  return {
    canAdd: (!rbac.userIsAgent || !isTrialAgent(currentAgent)) && !rbac.userIsContentWriter && !revoked,
    canSee: !rbac.userIsContentEditor && !revoked,
    canExplore: (rbac.userIsAdmin || rbac.userIsHR || rbac.userIsAgent) && !rbac.userIsContentWriter && !revoked,
    canExploreAll: (rbac.userIsAdmin || rbac.userIsHR || rbac.userIsAgent) && !rbac.userIsContentWriter && !revoked,
  };
};

const createPropertiesPermissions: PermissionFactory<'properties'> = (rbac, currentAgent) => {
  const revoked = hasRevocationStatus(currentAgent);

  return {
    canExplore: rbac.userIsAdmin || rbac.userIsCallCenter || rbac.userIsFinance || rbac.userIsContentEditor || rbac.userIsAgent,
    canExploreAll: rbac.userIsAdmin || rbac.userIsCallCenter || rbac.userIsFinance || rbac.userIsContentEditor || (rbac.userIsAgent && !revoked && !currentAgent?.inTrial),
  };
};

const createContactsPermissions: PermissionFactory<'contacts'> = (rbac, currentAgent) => {
  const revoked = hasRevocationStatus(currentAgent);

  return {
    canSee: rbac.userIsAdmin || rbac.userIsCallCenter || rbac.userIsFinance || rbac.userIsContentEditor || (rbac.userIsAgent && !revoked),
    canEdit: (agentIsRelatedToEntity = true) => rbac.userIsAdmin || rbac.userIsCallCenter || (rbac.userIsAgent && !revoked && agentIsRelatedToEntity),
    canSearch: rbac.userIsAdmin || rbac.userIsCallCenter || rbac.userIsContentEditor || (rbac.userIsAgent && !revoked && !currentAgent?.inTrial),
  };
};

const createContactServicesPermissions: PermissionFactory<'contactServices'> = (rbac, currentAgent) => {
  const revoked = hasRevocationStatus(currentAgent);

  return {
    canSee: rbac.userIsAdmin || rbac.userIsCallCenter || rbac.userIsAgent,
    canAdd: (agentIsRelatedToEntity = true) => rbac.userIsAdmin || rbac.userIsCallCenter || (rbac.userIsAgent && !revoked && agentIsRelatedToEntity),
    canDelete: (agentIsRelatedToEntity = true) => rbac.userIsAdmin || rbac.userIsCallCenter || (rbac.userIsAgent && !revoked && agentIsRelatedToEntity),
  };
};

const createContactOptInsPermissions: PermissionFactory<'contactOptIns'> = (rbac, currentAgent) => {
  const revoked = hasRevocationStatus(currentAgent);

  return {
    canSee: rbac.userIsAdmin || rbac.userIsCallCenter || rbac.userIsAgent,
    canAdd: (agentIsRelatedToEntity = true) => rbac.userIsAdmin || rbac.userIsCallCenter || (rbac.userIsAgent && !revoked && agentIsRelatedToEntity),
    canDelete: (agentIsRelatedToEntity = true) => rbac.userIsAdmin || rbac.userIsCallCenter || (rbac.userIsAgent && !revoked && agentIsRelatedToEntity),
  };
};

const createContactConcessionsPermissions: PermissionFactory<'contactConcessions'> = (rbac, currentAgent) => {
  const revoked = hasRevocationStatus(currentAgent);

  return {
    canSee: rbac.userIsAdmin || rbac.userIsCallCenter || rbac.userIsFinance || rbac.userIsAgent,
    canAdd: (agentIsRelatedToEntity = true) => rbac.userIsAdmin || rbac.userIsCallCenter || rbac.userIsFinance || (rbac.userIsAgent && !revoked && agentIsRelatedToEntity),
    canDelete: (agentIsRelatedToEntity = true) => rbac.userIsAdmin || rbac.userIsCallCenter || rbac.userIsFinance || (rbac.userIsAgent && !revoked && agentIsRelatedToEntity),
  };
};

const createMarketingPermissions: PermissionFactory<'marketing'> = (rbac, currentAgent) => ({
  canSeeSuggestions: rbac.userIsAdmin || rbac.userIsMarketing || !!currentAgent?.configuration?.marketing?.isCanvaAccessEnabled,
});

export const permissionFactories = {
  rumors: createRumorsPermissions,
  jobApplications: createJobApplicationsPermissions,
  properties: createPropertiesPermissions,
  contacts: createContactsPermissions,
  contactServices: createContactServicesPermissions,
  contactOptIns: createContactOptInsPermissions,
  contactConcessions: createContactConcessionsPermissions,
  marketing: createMarketingPermissions,
} satisfies {
  [T in UserPermissionGroup]: PermissionFactory<T>;
};
